import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Box,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ExitToApp, Menu as MenuIcon, Business as BusinessIcon, TrendingUp, CardGiftcard, ManageAccounts, NotificationsNoneOutlined } from "@mui/icons-material";
import { useAuth } from "../providers/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { EnterprisesAPI } from "../api/enterprises";
import { ROUTES } from "../routes";
import ReferralDialog from "./ReferralDialog";
import { NotificationsAPI } from '../api/notifications';

function formatRoleLabel(role) {
  return String(role || "Member")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function getDefaultAuthBase() {
  const envBase = process.env.REACT_APP_AUTH_URL ? process.env.REACT_APP_AUTH_URL.replace(/\/+$/, '') : '';
  if (envBase) return envBase;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
      return 'http://localhost:3000';
    }
  }
  return 'https://auth.sushaservices.com';
}

function getUserRoleManagementUrl() {
  return `${getDefaultAuthBase()}/user-role-management`;
}

export default function TopBar({ onMenuClick }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  const [referralDialogOpen, setReferralDialogOpen] = useState(false);
  const [demoRemainingLabel, setDemoRemainingLabel] = useState("");
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationUnread, setNotificationUnread] = useState(0);

  const isAuthenticated = !!auth?.user;
  const role = auth?.user?.role;
  const isDemo = auth?.user?.is_demo === true;
  const demoExpiresAt = auth?.user?.demo_expires_at;
  const userName = auth?.user?.full_name || auth?.user?.name || "User";
  const roleLabel = useMemo(() => formatRoleLabel(role), [role]);
  const open = Boolean(anchorEl);
  const canReceiveNotifications = isAuthenticated && !["super", "superadmin", "partner"].includes(role);

  useEffect(() => {
    if (isAuthenticated && role !== "super" && role !== "superadmin" && role !== "partner") {
      EnterprisesAPI.getMyEnterprise()
        .then((res) => setEnterpriseInfo(res?.data?.data || null))
        .catch(() => setEnterpriseInfo(null));
    }
  }, [isAuthenticated, role]);

  const loadNotifications = React.useCallback(async () => {
    if (!canReceiveNotifications) { setNotifications([]); setNotificationUnread(0); return; }
    try { const res = await NotificationsAPI.mine(); const data = res?.data?.data || {}; setNotifications(data.notifications || []); setNotificationUnread(Number(data.unread_count || 0)); return data; } catch { return null; /* notification delivery must not block navigation */ }
  }, [canReceiveNotifications]);

  useEffect(() => { void loadNotifications(); const interval = canReceiveNotifications ? setInterval(() => void loadNotifications(), 120000) : null; return () => { if (interval) clearInterval(interval); }; }, [canReceiveNotifications, loadNotifications]);

  const openNotifications = async (event) => {
    setNotificationAnchor(event.currentTarget);
    const data = await loadNotifications();
    const unreadStored = (data?.notifications || []).filter((item) => !item.is_system && !item.read_at).map((item) => item.id);
    if (unreadStored.length) { void NotificationsAPI.markRead(unreadStored); setNotifications((items) => items.map((item) => unreadStored.includes(item.id) ? { ...item, read_at: new Date().toISOString() } : item)); setNotificationUnread((count) => Math.max(0, count - unreadStored.length)); }
  };

  useEffect(() => {
    if (!isDemo || !demoExpiresAt) {
      setDemoRemainingLabel("");
      return undefined;
    }

    const formatRemaining = () => {
      const expiresAtMs = Date.parse(demoExpiresAt);
      if (Number.isNaN(expiresAtMs)) return "00:00";
      const remainingMs = Math.max(0, expiresAtMs - Date.now());
      const totalSec = Math.floor(remainingMs / 1000);
      const minutes = String(Math.floor(totalSec / 60)).padStart(2, "0");
      const seconds = String(totalSec % 60).padStart(2, "0");
      return `${minutes}:${seconds}`;
    };

    setDemoRemainingLabel(formatRemaining());
    const timer = setInterval(() => setDemoRemainingLabel(formatRemaining()), 1000);
    return () => clearInterval(timer);
  }, [isDemo, demoExpiresAt]);

  const isActiveTopNav = (path) => location.pathname.startsWith(path);

  const handleLogout = async () => {
    setAnchorEl(null);
    await auth.logout();
  };

  const handleLogoClick = () => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    if (role === "super" || role === "superadmin") {
      navigate(ROUTES.SUPERADMIN);
      return;
    }
    if (role === "partner") {
      navigate(ROUTES.PARTNER);
      return;
    }
    navigate(ROUTES.DASHBOARD);
  };

  const showPublicActions =
    !isAuthenticated &&
    (location.pathname.startsWith(ROUTES.LOGIN) ||
      location.pathname.startsWith(ROUTES.REGISTER) ||
      location.pathname.startsWith(ROUTES.PRICING));

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: "white",
        borderBottom: "1px solid #e2e8f0",
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 70 }, px: { xs: 2, sm: 3 } }}>
        {isMobile && isAuthenticated && (
          <IconButton edge="start" onClick={onMenuClick} sx={{ mr: 2, color: "#475569" }}>
            <MenuIcon />
          </IconButton>
        )}

        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={handleLogoClick}
          role="button"
          aria-label="Go to dashboard"
        >
          <Box
            component="img"
            src="/company-logo.png"
            alt="Susha Assets Management"
            sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, mr: { xs: 0, sm: 2 } }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#1e293b",
              fontSize: "1.25rem",
              display: { xs: "none", sm: "block" },
            }}
          >
            Susha Asset Management
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {isAuthenticated && (
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1, mr: 2 }}>
            {role === "enterprise_admin" && (
              <Chip
                label="Dashboard"
                onClick={() => navigate(ROUTES.DASHBOARD)}
                variant={isActiveTopNav(ROUTES.DASHBOARD) ? "filled" : "outlined"}
                color={isActiveTopNav(ROUTES.DASHBOARD) ? "primary" : "default"}
                sx={{ fontWeight: 600 }}
              />
            )}
            {role === "enterprise_admin" && (
              <Chip
                label="Pricing"
                onClick={() => navigate(ROUTES.PRICING)}
                variant={isActiveTopNav(ROUTES.PRICING) ? "filled" : "outlined"}
                color={isActiveTopNav(ROUTES.PRICING) ? "primary" : "default"}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        )}

        {isAuthenticated && (
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 0.5, mr: 0.75 }}>
            {role === "enterprise_admin" && (
              <Button
                size="small"
                onClick={() => navigate(ROUTES.DASHBOARD)}
                sx={{
                  textTransform: "none",
                  minWidth: "fit-content",
                  px: 1.15,
                  py: 0.5,
                  borderRadius: 999,
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  border: "1px solid",
                  borderColor: isActiveTopNav(ROUTES.DASHBOARD) ? "#1d4ed8" : "#cbd5e1",
                  bgcolor: isActiveTopNav(ROUTES.DASHBOARD) ? "#dbeafe" : "#f8fafc",
                  color: isActiveTopNav(ROUTES.DASHBOARD) ? "#1e3a8a" : "#475569",
                  lineHeight: 1,
                  "&:hover": {
                    borderColor: "#1d4ed8",
                    bgcolor: "#eff6ff",
                  },
                }}
              >
                Dashboard
              </Button>
            )}
            {role === "enterprise_admin" && (
              <Button
                size="small"
                onClick={() => navigate(ROUTES.PRICING)}
                sx={{
                  textTransform: "none",
                  minWidth: "fit-content",
                  px: 1.15,
                  py: 0.5,
                  borderRadius: 999,
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  border: "1px solid",
                  borderColor: isActiveTopNav(ROUTES.PRICING) ? "#1d4ed8" : "#cbd5e1",
                  bgcolor: isActiveTopNav(ROUTES.PRICING) ? "#dbeafe" : "#f8fafc",
                  color: isActiveTopNav(ROUTES.PRICING) ? "#1e3a8a" : "#475569",
                  lineHeight: 1,
                  "&:hover": {
                    borderColor: "#1d4ed8",
                    bgcolor: "#eff6ff",
                  },
                }}
              >
                Pricing
              </Button>
            )}
          </Box>
        )}

        {showPublicActions && (
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
            <Button
              onClick={() => navigate(ROUTES.PRICING)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: isActiveTopNav(ROUTES.PRICING) ? "#054693" : "#475569",
                minWidth: 0,
                px: { xs: 1, sm: 1.5 },
              }}
            >
              Pricing
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate(ROUTES.DEMO_EXPLORER)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 999,
                px: { xs: 1.4, sm: 2.2 },
                py: 0.75,
                fontSize: { xs: "0.74rem", sm: "0.82rem" },
                whiteSpace: "nowrap",
                bgcolor: "#2563eb",
                "&:hover": { bgcolor: "#1d4ed8" },
              }}
            >
              Watch Sandbox Demo
            </Button>
          </Box>
        )}

        {isAuthenticated && (
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
            {isDemo && (
              <Chip
                label={`Demo ${demoRemainingLabel || "30:00"}`}
                size="small"
                sx={{
                  bgcolor: "#fef3c7",
                  color: "#92400e",
                  border: "1px solid #fcd34d",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  display: { xs: "none", sm: "inline-flex" },
                }}
              />
            )}
            {canReceiveNotifications && <><IconButton onClick={openNotifications} aria-label="Open notifications" sx={{ color: '#475569' }}><Badge badgeContent={notificationUnread} color="error" max={9}><NotificationsNoneOutlined /></Badge></IconButton><Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { mt: 1, width: { xs: 'calc(100vw - 24px)', sm: 390 }, maxHeight: 470, borderRadius: 2.5, border: '1px solid #e2e8f0' } }}><Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e7eef7' }}><Typography fontWeight={800}>Notifications</Typography><Typography variant="caption" color="text.secondary">Warranty, health signals, and Susha updates</Typography></Box>{notifications.length ? notifications.slice(0, 12).map((item) => <MenuItem key={`${item.category}-${item.id}`} onClick={() => { setNotificationAnchor(null); if (item.action_url) navigate(item.action_url); }} sx={{ alignItems: 'flex-start', whiteSpace: 'normal', py: 1.35, borderBottom: '1px solid #f0f4f8' }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.severity === 'critical' ? '#dc2626' : item.severity === 'warning' ? '#d97706' : '#2563eb', mt: .7, mr: 1.25, flexShrink: 0 }} /><Box><Typography fontSize={13.5} fontWeight={750}>{item.title}</Typography><Typography fontSize={12.5} color="text.secondary" sx={{ mt: .25, lineHeight: 1.45 }}>{item.message}</Typography></Box></MenuItem>) : <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}><Typography variant="body2">You are all caught up.</Typography></Box>}</Menu></>}

            <Box
              sx={{
                textAlign: "right",
                mr: 1,
                minWidth: 0,
                display: "block",
                maxWidth: { xs: 160, sm: 260 },
              }}
            >
              <Typography
                variant="body2"
                fontWeight={700}
                color="#1e293b"
                sx={{
                  lineHeight: 1.2,
                  fontSize: { xs: "0.72rem", sm: "0.88rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {enterpriseInfo?.name || auth?.user?.enterprise_name || "Enterprise"}
              </Typography>
              <Typography
                variant="caption"
                color="#64748b"
                sx={{
                  display: "block",
                  lineHeight: 1.2,
                  fontSize: { xs: "0.64rem", sm: "0.75rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {roleLabel}
              </Typography>
            </Box>

            <IconButton data-tour="top-account" onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "#3b82f6",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                }}
              >
                {userName[0].toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 280,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                },
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  bgcolor: "#f8fafc",
                  borderBottom: "1px solid #e2e8f0",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 0.5,
                    fontSize: "0.95rem",
                  }}
                >
                  {userName}
                </Typography>
                <Chip
                  label={roleLabel}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    bgcolor: "#e0f2fe",
                    color: "#075985",
                    "& .MuiChip-label": { px: 1.5 },
                  }}
                />
                {enterpriseInfo?.name && (
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1 }}>
                    {enterpriseInfo.name}
                  </Typography>
                )}
              </Box>

              {role === "enterprise_admin" && (
                <>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      setReferralDialogOpen(true);
                    }}
                    sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem" }}
                  >
                    <CardGiftcard fontSize="small" sx={{ mr: 2, color: "#667eea" }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Refer to Others
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Invite a partner or branch user
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      window.location.assign(getUserRoleManagementUrl());
                    }}
                    sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem" }}
                  >
                    <ManageAccounts fontSize="small" sx={{ mr: 2, color: "#64748b" }} />
                    User & Role Management
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      navigate(ROUTES.PRICING);
                    }}
                    sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem" }}
                  >
                    <TrendingUp fontSize="small" sx={{ mr: 2, color: "#64748b" }} />
                    Billing
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setAnchorEl(null);
                      navigate(ROUTES.ENTERPRISE_PROFILE);
                    }}
                    sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem" }}
                  >
                    <BusinessIcon fontSize="small" sx={{ mr: 2, color: "#64748b" }} />
                    Company Profile
                  </MenuItem>
                </>
              )}
              {role === "partner" && (
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    navigate(ROUTES.PARTNER);
                  }}
                  sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem" }}
                >
                  <BusinessIcon fontSize="small" sx={{ mr: 2, color: "#64748b" }} />
                  Partner Portal
                </MenuItem>
              )}

              <MenuItem
                onClick={handleLogout}
                sx={{ py: 1.5, px: 2.5, fontSize: "0.9rem", borderTop: "1px solid #e2e8f0" }}
              >
                <ExitToApp fontSize="small" sx={{ mr: 2, color: "#64748b" }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        )}

        <ReferralDialog
          open={referralDialogOpen}
          onClose={() => setReferralDialogOpen(false)}
        />
      </Toolbar>
    </AppBar>
  );
}
